import { Box } from "@mui/material";
import SensorIcon from "./sensorBox/sensorIcon.component";
import SensorInfo from "./sensorBox/sensorInfo.component";
import { sensorBoxStyle } from "../../styles/commonStyles";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetDraggableMode,
  setDraggableMode,
  setLocationMode,
} from "../../store/features/customer/marker/dragModeSlice";
import {
  sensorIsHoverEnd,
  sensorIsHoverStart,
  resetUpdateSensorState,
  updateSensorStart,
} from "../../store/features/customer/sensor/sensorSlice";
import {
  resetMarkerData,
  setMarkerData,
} from "../../store/features/customer/marker/markerSlice";
import SensorControls from "./sensorBox/sensorControls.component";

const isSameId = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const SensorBox = ({ instrumentsData, field }) => {
  const dispatch = useDispatch();

  const [isHover, setIsHover] = useState(false);
  const { error, isLoading: isSensorSaving, success } = useSelector(
    (state) => state.sensor
  );
  const { hoveredSensorId } = useSelector((state) => state.sensor?.isHover);
  const { isActive: isDragModeActive, draggableSensorId } = useSelector(
    (state) => state.dragMode
  );
  const isCurrentSensorHovered = isSameId(hoveredSensorId, instrumentsData.id);
  const isAnySensorHovered = !!hoveredSensorId;
  const isSensorInDragMode = isSameId(draggableSensorId, instrumentsData.id);
  const hasPendingPositionSave = useRef(false);

  const handleHoverSensor = () => {
    setIsHover(true);
    dispatch(sensorIsHoverStart(instrumentsData.id));
  };
  const handleStopHoverSensor = () => {
    setIsHover(false);
    dispatch(sensorIsHoverEnd());
  };

  const getOpacity = () => {
    if (isDragModeActive) {
      return isSensorInDragMode ? 1 : 0.6;
    }

    if (!isAnySensorHovered) return 1;
    return isCurrentSensorHovered ? 1 : 0.6;
  };

  const handleDragModeStart = (sensorId) => {
    hasPendingPositionSave.current = false;
    dispatch(resetUpdateSensorState());
    dispatch(setDraggableMode(sensorId));
  };

  const handleDragModeStop = () => {
    hasPendingPositionSave.current = false;
    dispatch(resetDraggableMode());
    dispatch(resetMarkerData());
    dispatch(setLocationMode(false));
  };

  const handleFirstPosition = (sensorId, position) => {
    hasPendingPositionSave.current = false;
    dispatch(resetUpdateSensorState());
    const data = {
      sensorId,
      sensorType: instrumentsData.sensor_type?.id || instrumentsData.sensor_type,
      nickname: instrumentsData.nickname,
      gps_position: position,
    };
    dispatch(setDraggableMode(sensorId));
    dispatch(setMarkerData(data));
  };

  const handleSavePosition = (sensorData, token, sensorId) => {
    hasPendingPositionSave.current = true;
    dispatch(
      updateSensorStart({
        data: sensorData,
        accessToken: token,
        sensorId: sensorId,
        operationType: "position",
      })
    );
  };

  useEffect(() => {
    if (error && !isSensorSaving && hasPendingPositionSave.current) {
      hasPendingPositionSave.current = false;
      return;
    }

    if (
      success &&
      !isSensorSaving &&
      isSensorInDragMode &&
      hasPendingPositionSave.current
    ) {
      hasPendingPositionSave.current = false;
      setIsHover(false);
      dispatch(setLocationMode(false));
      dispatch(sensorIsHoverEnd());
      dispatch(resetDraggableMode());
      dispatch(resetMarkerData());
    }
  }, [dispatch, error, isSensorInDragMode, isSensorSaving, success]);

  return (
    <Box
      sx={{
        ...sensorBoxStyle(),
        opacity: getOpacity(),
      }}
      onMouseEnter={handleHoverSensor}
      onMouseLeave={handleStopHoverSensor}
    >
      <SensorIcon instrumentsData={instrumentsData} />
      <Box display="flex" flexDirection="column" width="100%" height="100%">
        <SensorInfo instrumentsData={instrumentsData} />
        <SensorControls
          instrumentsData={instrumentsData}
          field={field}
          isHover={isHover}
          onDragModeStart={handleDragModeStart}
          onDragModeStop={handleDragModeStop}
          onFirstPosition={handleFirstPosition}
          onSavePosition={handleSavePosition}
          dispatch={dispatch}
        />
      </Box>
    </Box>
  );
};

export default SensorBox;
